/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { BsPlayFill } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import ReactTooltip from "react-tooltip";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
} from "recoil";
import { v4 } from "uuid";
import { PlayButtonAtom } from "../../atoms/PlayButtonAtom";
import { HelpVideo } from "../HelpVideo";

const PlayButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 12px;
  border-radius: 5px;
  border: 1px solid #dddddd;
  margin-right: 15px;
  cursor: pointer;
  > div > svg {
    transform: rotate(-90deg);
  }
`;
interface ModalAtomValue {
  id: string;
  component: JSX.Element | null;
  title?: string | null;
  removeHeader?: boolean;
  showPlayButton?: boolean;
  onClose?: () => any;
}

interface ModalHook {
  /**
   * `pushModal` has a fire-and-forget mode and it also has an awaitable mode.
   *
   * For the fire and forget mode:
   * ```js
   * const modal = useModal();
   * modal.pushModal(<div>Hello</div>);
   * ```
   *
   * For an awaitable interaction:
   * ```js
   * const modal = useModal();
   *
   * const awaitableInteraction =
   *  modal.pushModal(interaction =>
   *   <div onClick={() => interaction("clicked")}>
   *    Click me!
   *   </div>
   *  )
   *
   * const interactionResult = await awaitableInteraction;
   * console.log(interactionResult); // "clicked"
   * ```
   */
  pushModal: {
    (
      componentOrFunction: JSX.Element,
      title?: string | JSX.Element | undefined,
      removeHeader?: boolean,
      showPlayButton?: boolean,
      onClose?: () => any
    ): null;
    (
      componentOrFunction: ElementWithPromise,
      title?: string | undefined,
      removeHeader?: boolean,
      showPlayButton?: boolean,
      onClose?: () => any
    ): Promise<unknown>;
  };
  popModal: () => void;
  popAll: () => void;
}

export const ModalAtom = atom<ModalAtomValue[]>({
  key: "ModalAtom",
  default: [],
});

type PromiseResolve = (value: unknown) => void;
type ElementWithPromise = (onModalInteraction: PromiseResolve) => JSX.Element;

export function useModal(): ModalHook {
  const [modalAtom, setModalAtom] = useRecoilState(ModalAtom);

  const playButtonAtomReset = useResetRecoilState(PlayButtonAtom);

  function pushModalSignature(
    componentOrFunction: JSX.Element,
    title?: string,
    removeHeader?: boolean,
    showPlayButton?: boolean,
    onClose?: () => any
  ): null;
  function pushModalSignature(
    componentOrFunction: ElementWithPromise,
    title?: string,
    removeHeader?: boolean,
    showPlayButton?: boolean,
    onClose?: () => any
  ): Promise<unknown>;
  function pushModalSignature(
    componentOrFunction: JSX.Element | ElementWithPromise,
    title?: string,
    removeHeader?: boolean,
    showPlayButton?: boolean,
    onClose?: () => any
  ): null | Promise<unknown> {
    if (typeof componentOrFunction === "function") {
      const componentFunction = componentOrFunction as (
        onModalInteraction: (value: unknown) => void
      ) => JSX.Element;
      useModal();
      let modalResolve: (value: unknown) => void;
      const modalPromise = new Promise((resolve) => {
        modalResolve = resolve;

        const component = componentFunction(modalResolve);
        // This is like this because this hook stores the modal stack from before the current is opened,
        // so modalAtom is one modal behind, currentState otherwise is always updated
        setModalAtom((currentState) => [
          ...currentState,
          {
            id: v4(),
            component,
            title,
            removeHeader,
            showPlayButton,
            onClose,
          },
        ]);
      });

      return modalPromise;
    }

    const component = componentOrFunction as JSX.Element;
    // This is like this because this hook stores the modal stack from before the current is opened,
    // so modalAtom is one modal behind, currentState otherwise is always updated
    setModalAtom((currentState) => [
      ...currentState,
      {
        id: v4(),
        component,
        title,
        removeHeader,
        showPlayButton,
        onClose,
      },
    ]);

    return null;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pushModal = useCallback(pushModalSignature, [modalAtom]);

  const popModal = useCallback(() => {
    // Clear button play atom
    playButtonAtomReset();
    // This is like this because this hook stores the modal stack from before the current is opened,
    // so modalAtom is one modal behind, currentState otherwise is always updated
    setModalAtom((currentState) => {
      const state = [...currentState];

      state.pop();

      return state;
    });
  }, [setModalAtom]);

  const popAll = useCallback(() => {
    // Clear button play atom
    playButtonAtomReset();
    setModalAtom([]);
  }, [setModalAtom]);

  return { pushModal, popModal, popAll };
}

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: #00000066;
  z-index: 9999;
`;

interface ModalBodyProps {
  removeHeader?: boolean;
  isJSXTitle?: boolean;
}

const ModalBody = styled(motion.div)<ModalBodyProps>`
  align-items: center;
  position: relative;
  width: auto;
  height: auto;
  display: grid;
  ${(props) =>
    props.removeHeader
      ? css`
          grid-template: "comp comp comp";
          grid-template-rows: 1fr;
        `
      : css`
          grid-template:
            "title title close"
            "comp comp comp";
          grid-template-rows: ${props.isJSXTitle ? "auto auto" : "12px auto"};
        `}
  grid-template-columns: auto auto auto;
  border-radius: 5px;
  padding: ${(props) => (props.removeHeader ? 0 : 25)}px;
  z-index: 9999;
  box-shadow: 0px 3px 20px #00000033;

  @media (max-width: 1024px) {
    width: 100vw;
    height: 100vh;

    padding: 0;

    overflow-y: auto;
    overflow-x: hidden;

    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
    justify-content: start !important;
  }

  background-color: white;

  .icon-button {
    min-width: 42px;
    max-width: 42px;
    min-height: 42px;
    max-height: 42px;
    margin-right: 15px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #fff;

    border-radius: 5px;
    border: 1px solid #dddddd;

    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;

      color: #555555;
    }

    .tooltip-text {
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0px;
      color: #ffffff;
    }
  }
`;

const Component = styled.div<{ currentPath?: string }>`
  grid-area: comp;

  @media screen and (max-width: 1024px) {
    width: fit-content;
    height: ${(props) =>
      props.currentPath === "/emails"
        ? "calc(100vh - 275px)"
        : "calc(100vh - 75px)"};
  }
`;

const Title = styled.div`
  grid-area: title;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #2f2f2f;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.18px;

  @media screen and (max-width: 1024px) {
    width: 100%;
    padding: 25px;
    border-bottom: 1px solid;
    border-color: inherit;
  }
`;

const CloseIconContainer = styled.div`
  grid-area: close;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 5px;
  cursor: pointer;
  height: 25px;

  @media screen and (max-width: 1024px) {
    padding: 0;

    position: absolute;
    top: 25px;
    right: 25px;
  }
`;

const CloseIconButton = styled.img`
  cursor: pointer;
`;

const ModalBodyVariants: Variants = {
  closed: {
    y: 800,
    opacity: 0,
  },
  open: {
    y: 0,
    opacity: 1,
  },
};

export default function Modal(): JSX.Element {
  const [modalAtom, setModalAtom] = useRecoilState(ModalAtom);

  const router = useRouter();

  const currentPath = router.pathname;

  const playButton = useRecoilValue(PlayButtonAtom);
  const playButtonAtomReset = useResetRecoilState(PlayButtonAtom);

  function handleCloseModal(currentId: string) {
    // playButtonAtomReset();
    setModalAtom(modalAtom.filter(({ id }) => currentId !== id));
  }

  return (
    <AnimatePresence>
      {modalAtom.map((modal) => (
        <Root key={modal.id} className="modalRoot">
          <ModalBody
            variants={ModalBodyVariants}
            initial="closed"
            animate="open"
            exit="closed"
            removeHeader={modal.removeHeader}
            isJSXTitle={typeof modal?.title !== ("string" && "undefined")}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {!modal.removeHeader && (
              <>
                <CloseIconContainer>
                  {playButton && modal.showPlayButton && (
                    <div
                      className="icon-button"
                      onClick={() =>
                        setModalAtom((currentState) => [
                          ...currentState,
                          {
                            id: v4(),
                            component: <HelpVideo {...playButton} />,
                            title: playButton.title,
                          },
                        ])
                      }
                      data-tip
                      data-for="openVideo"
                    >
                      <BsPlayFill />

                      <ReactTooltip
                        id="openVideo"
                        place="bottom"
                        effect="solid"
                        globalEventOff="click"
                        backgroundColor="#333333"
                        css={css`
                          width: fit-content !important;
                          padding: 6px 8px !important;
                        `}
                      >
                        <div className="tooltip-text">
                          Watch Quick Help Video
                        </div>
                      </ReactTooltip>
                    </div>
                  )}

                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCloseModal(modal.id);
                      modal.onClose?.();
                    }}
                  >
                    <IoMdClose color="#868482" size={25} />
                  </div>
                </CloseIconContainer>

                <Title>{modal?.title}</Title>
              </>
            )}
            <Component currentPath={currentPath}>{modal.component}</Component>
          </ModalBody>
        </Root>
      ))}
    </AnimatePresence>
  );
}
